import React, { createContext, useContext, useState } from "react";

import { noop } from "lodash/fp";
import { DateTime } from "luxon";
import { useSearchParams } from "react-router-dom";

import { DateSelectorChangeEvent } from "./DateSelector/DateSelector";
import { GROUPED_PRESETS } from "./DateSelector/utils";
import { MeetingPolicyMetric, ToggleState } from "./types";

const DEFAULT_PRESET = GROUPED_PRESETS.month[0];

interface UseMeetingPolicy {
  startDate: DateTime | null;
  endDate: DateTime | null;
  rangePreset: DateSelectorChangeEvent;
  setDateRange: (event: DateSelectorChangeEvent) => void;
  setToggleState: (state: ToggleState) => void;
  toggleState: ToggleState;
  selectedMetric: MeetingPolicyMetric;
  noDataCount: number;
  reportNoData: () => void;
}

const DEFAULT_CONTEXT: UseMeetingPolicy = {
  startDate: null,
  endDate: null,
  rangePreset: DEFAULT_PRESET,
  setDateRange: noop,
  setToggleState: noop,
  toggleState: { chartLabels: true, meetingCount: true, readAverage: false },
  selectedMetric: "meetingScore" as const,
  noDataCount: 0,
  reportNoData: noop,
};

const meetingPolicyContext = createContext(DEFAULT_CONTEXT);

export const MeetingPolicyProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const meetingPolicy = useProvideMeetingPolicy();
  return (
    <meetingPolicyContext.Provider value={meetingPolicy}>
      {children}
    </meetingPolicyContext.Provider>
  );
};

export const useMeetingPolicy = () => useContext(meetingPolicyContext);

const useProvideMeetingPolicy = (): UseMeetingPolicy => {
  const [queryParams] = useSearchParams();

  const currentTab = queryParams.get("tab") as MeetingPolicyMetric;
  const selectedMetric: MeetingPolicyMetric =
    currentTab ?? ("meetingScore" as const);

  const [noDataCount, setNoDataCount] = useState(0);
  const reportNoData = () => setNoDataCount(noDataCount + 1);

  const [toggleState, setToggleState] = useState<ToggleState>({
    chartLabels: true,
    meetingCount: true,
    readAverage: false,
  });

  const [rangePreset, setDateRange] =
    useState<DateSelectorChangeEvent>(DEFAULT_PRESET);

  return {
    startDate: rangePreset.startDate,
    endDate: rangePreset.endDate,
    selectedMetric,
    noDataCount,
    reportNoData,

    rangePreset,
    setDateRange,
    setToggleState,
    toggleState,
  };
};
export default useMeetingPolicy;
